import React, { useState } from "react";
import Header from "../components/Header";
import Signature from "../components/Signature";
import { useLoaderData, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import axios from "axios";
import { useTranslation } from "react-i18next";
import "../fonts/hebrewFont";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ConsentformHE = () => {
    const { t, i18n } = useTranslation("consentform");

    const currentLang = i18n.language || "he";
    const dir = currentLang === "he" || currentLang === "ar" ? "rtl" : "ltr";

    const {
        firstName,
        surName,
        licenseNumber,
        institute,
        sessionId,
        role,
    } = useLoaderData();

    const navigate = useNavigate();

    const [user, setUser] = useState("");
    const [signature1, setSignature1] = useState(false);
    const [signature2, setSignature2] = useState(false);
    const [errorUser, setErrorUser] = useState("");

    const [firstNameGeneric, setFirstNameGeneric] = useState("");
    const [surNameGeneric, setSurNameGeneric] = useState("");
    const [licenseNumberGeneric, setLicenseNumberGeneric] = useState("");
    const [instituteGeneric, setInstituteGeneric] = useState("");

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString("en-GB");

    const isGenericUser = role === "Generic";

    const parentParagraphs = t("parentSection.paragraphs", {
        returnObjects: true,
    });

    const doctorDeclaration = t("doctorSection.declaration", {
        returnObjects: true,
    });

    const insertUserName = (value) => {
        if (value === "") {
            setErrorUser("error.required");
            setUser("");
        } else if (value.trim().split(/\s+/).length === 1) {
            setErrorUser("error.require2");
            setUser("");
        } else {
            setErrorUser("");
            setUser(value);
        }
    };

    const reverseText = (text) =>
        text
            .split("\n")
            .map((line) => line.split("").reverse().join(""))
            .join("\n");

    const getPdfText = (text) => {
        if (currentLang === "he" || currentLang === "ar") {
            return reverseText(text);
        }

        return text;
    };

    const generatePDF = async (
        user,
        signature1Url,
        signature2Url,
        date,
        sessionId
    ) => {
        const pdf = new jsPDF("p", "mm", "a4");

        const isRtl = currentLang === "he" || currentLang === "ar";
        const margin = isRtl ? 190 : 20;
        const align = isRtl ? "right" : "left";

        let y = 10;

        pdf.setFont("Heebo");
        pdf.setFontSize(14);

        pdf.text(getPdfText(t("formTitle")), margin, y, { align });
        y += 10;

        pdf.setFontSize(10);

        const bodyText = `
${t("parentSection.greeting")}

${parentParagraphs.join("\n\n")}

${t("parentSection.closing")}

${t("parentSection.team")}

${t("parentSection.contactText")} ${t("parentSection.email")}

${t("parentSection.phoneText")} ${t("parentSection.phone")}
`;

        const splitText = pdf.splitTextToSize(getPdfText(bodyText), 170);
        pdf.text(splitText, margin, y, { align });
        y += splitText.length * 4 + 10;

        pdf.setFontSize(12);
        pdf.text(
            getPdfText(`${t("parentApprovalSection.parentNameLabel")}: ${user}`),
            margin,
            y,
            { align }
        );
        y += 10;

        if (signature1Url) {
            pdf.text(getPdfText(`${t("doctorSection.signature")}:`), margin, y, {
                align,
            });
            pdf.addImage(signature1Url, "PNG", isRtl ? margin - 100 : margin, y - 7, 50, 20);
            y += 15;
        }

        pdf.text(getPdfText(`${t("doctorSection.dateLabel")}: ${date}`), margin, y, {
            align,
        });
        y += 15;

        pdf.setFontSize(10);

        const doctorFullName = isGenericUser
            ? `${firstNameGeneric} ${surNameGeneric}`
            : `${firstName} ${surName}`;

        const doctorLicenseNumber = isGenericUser
            ? licenseNumberGeneric
            : licenseNumber;

        const doctorInstitute = isGenericUser ? instituteGeneric : institute;

        const doctorText = `
${t("doctorSection.title")}

${doctorDeclaration.join("\n\n")}

${t("doctorSection.doctorNameLabel")}: ${doctorFullName}

${t("doctorSection.licenseNumberLabel")}: ${doctorLicenseNumber}

שם בית החולים: ${doctorInstitute}
`;

        const doctorSplit = pdf.splitTextToSize(getPdfText(doctorText), 170);
        pdf.text(doctorSplit, margin, y, { align });
        y += doctorSplit.length * 4 + 10;

        if (signature2Url) {
            pdf.text(getPdfText(`${t("doctorSection.signature")}:`), margin, y, {
                align,
            });
            pdf.addImage(signature2Url, "PNG", isRtl ? margin - 100 : margin, y - 7, 50, 20);
            y += 25;
        }

        pdf.text(getPdfText(`${t("doctorSection.dateLabel")}: ${date}`), margin, y, {
            align,
        });

        const pdfBlob = pdf.output("blob");
        const formData = new FormData();

        formData.append("img_consent", pdfBlob, `consent_form${sessionId}.pdf`);

        try {
            const url = !isGenericUser
                ? `/dashboard/consent_document/${sessionId}`
                : `/dashboard/consent_document/${sessionId}?name=${encodeURIComponent(
                    firstNameGeneric
                )}&licenseNumber=${encodeURIComponent(
                    licenseNumberGeneric
                )}&institute=${encodeURIComponent(instituteGeneric)}`;

            const response = await axios.post(url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            console.log("הקובץ נשלח בהצלחה לשרת", response.data);
            return true;
        } catch (error) {
            console.error("שגיאה בשליחת הקובץ:", error);
            return false;
        }
    };

    const handleSubmit = async () => {
        const success = await generatePDF(
            user,
            signature1,
            signature2,
            formattedDate,
            sessionId
        );

        if (success) {
            navigate(`/Questionnaire/${sessionId}`);
        }
    };

    return (
        <div
            className="w-full h-screen text-Black font-[heebo] overflow-y-scroll bg-white pb-3"
            dir={dir}
            lang={currentLang}
        >
            <div className="relative w-full">
                <Header />
                <LanguageSwitcher className="absolute left-3 top-5" />
            </div>

            <div id="consent-form" style={{ direction: dir }}>
                <p
                    className="font-bold text-[18px] mt-5 text-center"
                    style={{ lineHeight: "28px" }}
                >
                    {t("formTitle")}
                </p>

                <div
                    className="p-8 text-[12px] pt-0 space-y-2"
                    style={{
                        direction: dir,
                        textAlign: dir === "rtl" ? "right" : "left",
                    }}
                >
                    <p className="font-bold text-[12px]" style={{ lineHeight: "16px" }}>
                        {t("parentSection.greeting")}
                    </p>

                    <div className="font-normal space-y-2" style={{ lineHeight: "16px" }}>
                        {parentParagraphs.map((paragraph, index) => (
                            <p
                                key={index}

                                className=
                                {
                                    index === parentParagraphs.length - 1
                                        ? "whitespace-pre-line font-bold text-[12px]"
                                        : "whitespace-pre-line"
                                }
                            >
                                {paragraph}
                            </p>
                        ))}
                    </div>

                    <p className="font-bold" style={{ lineHeight: "16px" }}>
                        {t("parentSection.closing")}
                    </p>

                    <p className="font-bold" style={{ lineHeight: "16px" }}>
                        {t("parentSection.team")}
                    </p>

                    <p className="font-normal" style={{ lineHeight: "16px" }}>
                        {t("parentSection.contactText")}
                        <span>{t("parentSection.email")}</span>
                    </p>

                    <p className="font-normal" style={{ lineHeight: "16px" }}>
                        {t("parentSection.phoneText")} {t("parentSection.phone")}
                    </p>

                    <div className="w-full mb-1">
                        <label className="font-bold text-[12px] w-full">
                            {t("parentApprovalSection.parentNameLabel")}
                        </label>

                        <input
                            type="text"
                            required
                            className="bg-LightBlue rounded-lg px-4 py-2 border outline-0 border-LightBlue w-full focus:border-MidBlue"
                            placeholder={t("parentApprovalSection.fullNameLabel")}
                            onChange={(e) => insertUserName(e.currentTarget.value)}
                        />

                        {errorUser && (
                            <p className="text-Negative font-bold text-[12px]">
                                {t(errorUser)}
                            </p>
                        )}
                    </div>

                    <p className="font-bold text-[12px] w-full">
                        {t("doctorSection.signature")}
                    </p>

                    <Signature setSignature={setSignature1} />

                    <p
                        className="font-bold text-[12px]"
                        style={{ lineHeight: "16px", textAlign: dir === "rtl" ? "left" : "right" }}
                    >
                        {t("doctorSection.dateLabel")}: {formattedDate}
                    </p>

                    <p className="font-bold text-[12px]" style={{ lineHeight: "16px" }}>
                        {t("doctorSection.title")}
                    </p>

                    {doctorDeclaration.map((paragraph, index) => (
                        <p key={index} className="font-normal whitespace-pre-line" style={{ lineHeight: "16px" }}>
                            {paragraph}
                        </p>
                    ))}

                    {isGenericUser ? (
                        <>
                            <label className='font-bold text-[12px]' style={{ lineHeight: "16px" }}>
                                {t("doctorSection.doctorNameLabel")}:
                                <input
                                    type="text"
                                    required
                                    className='bg-LightBlue rounded-lg px-4 py-2 border outline-0 border-LightBlue w-full focus:border-MidBlue   '
                                    placeholder={t("doctorSection.doctorNameLabel")}
                                    value={firstNameGeneric}
                                    onChange={(e) => setFirstNameGeneric(e.target.value)}
                                />
                            </label>

                            <label className="font-bold text-[12px]" style={{ lineHeight: "16px" }}>
                                {t("doctorSection.licenseNumberLabel")}:
                                <input
                                    type="text"
                                    required
                                    className="bg-LightBlue rounded-lg px-4 py-2 border outline-0 border-LightBlue w-full focus:border-MidBlue"
                                    placeholder={t("doctorSection.licenseNumberLabel")}
                                    value={licenseNumberGeneric}
                                    onChange={(e) => setLicenseNumberGeneric(e.target.value)}
                                />
                            </label>

                            <label className="font-bold text-[12px]" style={{ lineHeight: "16px" }}>
                    {   t("doctorSection.HospitalName")}:
                                <input
                                    type="text"
                                    required
                                    className="bg-LightBlue rounded-lg px-4 py-2 border outline-0 border-LightBlue w-full focus:border-MidBlue"
                                    placeholder={t("doctorSection.HospitalName")}
                                    value={instituteGeneric}
                                    onChange={(e) => setInstituteGeneric(e.target.value)}
                                />
                            </label>
                        </>
                    ) : (
                        <>
                            <p className="font-bold text-[12px]" style={{ lineHeight: "16px" }}>
                                {t("doctorSection.doctorNameLabel")}:
                                <span className="font-normal text-Grey300 mx-1">
                                    {firstName} {surName}
                                </span>
                            </p>

                            <p className="font-bold text-[12px]" style={{ lineHeight: "16px" }}>
                                {t("doctorSection.licenseNumberLabel")}:
                                <span className="font-normal text-Grey300 mx-1">
                                    {licenseNumber}
                                </span>
                            </p>

                            <p className="font-bold text-[12px]" style={{ lineHeight: "16px" }}>
                                {   t("doctorSection.HospitalName")}:
                                <span className="font-normal text-Grey300 mx-1">
                                    {institute}
                                </span>
                            </p>
                        </>
                    )}
                     <p className="font-bold text-[12px] m" style={{ lineHeight: "16px" }}>
{t("doctorSection.signature")}</p>
                    <Signature setSignature={setSignature2} />

                    <p
                        className="font-bold text-[12px]"
                        style={{ lineHeight: "16px", textAlign: dir === "rtl" ? "left" : "right" }}
                    >
                        {t("doctorSection.dateLabel")}: {formattedDate}
                    </p>



                    {user && signature1 && signature2 ? (
                        <button
                            type="button"
                            className="bg-PraimeryBlue text-white font-bold text-lg w-full rounded-lg p-4"
                            onClick={handleSubmit}
                        >
                            אישור
                        </button>
                    ) : (
                        <button
                            className="bg-Grey100 text-Grey300 font-bold text-lg rounded-lg p-4 w-full"
                            type="button"
                            disabled
                        >
                            אישור
                        </button>
                    )}
                    <p
                        className="font-bold text-[12px]"
                        style={{ lineHeight: "16px", textAlign: "left" }}
                    >
                        מספר סידורי: {sessionId}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ConsentformHE;